import { useCallback, useEffect, useRef, useState } from 'react';
import { OAUTH_PROVIDERS } from '../../constants/oauthProviders';
import api from '../../services/api';
import { NavigateSameTab } from '../../utils/browserNavigation';
import {
    BuildOAuthCompleteBody,
    BuildOAuthStartBody,
    DescribeOAuthCompletionError,
    DescribeOAuthConnection,
    DescribeOAuthEnvironment,
    DescribeOAuthRegistration,
    DescribeOAuthScopes,
    ReadOAuthReturn,
    ResolveOAuthProviderForStatus,
    ResolveOAuthReturn,
    SavePendingOAuthState,
    StripOAuthReturn,
    TakePendingOAuthState,
} from '../../utils/oauthConnection';

/**
 * Conexões (Health → aba Sistema) — os quatro provedores de OAuth num padrão só (09/10, pedido do dono: "padronizar o
 * OAuth completo de todos os providers", com texto mínimo na tela e o detalhe em modal/accordion).
 *
 *  · UMA LINHA POR PROVEDOR, o mesmo componente para os quatro (`ConnectionRow`): nome, selo, a conta numa linha curta,
 *    Detalhes, Conectar/Reautenticar e Desconectar. TikTok Shop e Shopee só aparecem com `configured: true`. A Shopee
 *    tem uma palavra a mais, o AMBIENTE ("teste" ou "produção", 10/10) — sem descrição.
 *  · UM MODAL DA CONEXÃO (`ConnectionModal`): é a mensagem de "conectado" de todos — abre sozinho quando a autorização
 *    conclui (colando ou na volta automática) — e é também o "Detalhes" da linha: conta, validade, o accordion
 *    "Escopos" (o que o status traz em `scopes`; sem lista, o motivo) e o accordion "Detalhes" (avisos, URL a
 *    cadastrar).
 *  · UM MODAL DE COLAR (`PasteModal`): o mesmo para os quatro; os passos de cada provedor ficam atrás de "Como fazer".
 *  · Mensagens que não são "conectado" (desconectou, erro da volta automática, retorno recusado) vão para UM lugar: o
 *    aviso do cartão.
 *
 * O FLUXO não mudou. Mercado Livre e Bling: Conectar abre a autorização em aba nova com o `redirect_uri` da caixa do
 * cabeçalho, e a pessoa cola a URL de retorno (`{ callback_url }`). TikTok Shop e Shopee (`returnMode: 'automatic'`):
 * Conectar sai NA MESMA ABA com o `state` guardado nesta aba (`sessionStorage`); o callback do backend devolve o
 * navegador a `/health?tab=sistema&oauth_provider=<provedor>#oauth_return=<query>` e este cartão conclui — só com a
 * pendência desta aba, o `state` idêntico e uma vez só (`ResolveOAuthReturn`). Colar é o plano B de todos, em
 * Detalhes → "Colar URL de retorno".
 *
 * A identificação da conta do Mercado Livre (`identify=true`) é a chamada de sempre; nenhum outro provedor é chamado
 * daqui.
 */
const RETURN_REFUSAL_TEXTS = {
    // É também o que vê quem clicou em "Autorizar em outra aba" com a Redirect URL já certa: a aba nova volta sozinha
    // e não tem pendência (o `state` ficou na memória do modal da outra aba, que espera uma URL que não virá).
    no_pending:
        'Chegou um retorno de autorização que não começou nesta aba. Nada foi enviado — clique em Conectar. Se você veio de "Autorizar em outra aba", feche esta aba e use o Conectar: o retorno automático já funciona.',
    state_mismatch:
        'O retorno de autorização não corresponde ao Conectar desta aba. Nada foi enviado — clique em Conectar de novo.',
};

/** O `sessionStorage` desta aba — ou nada, onde o navegador o nega (janela privada, política do site). */
function tabStorage() {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

const TEXT_TONES = {
    ok: 'text-success-text dark:text-success-300',
    warn: 'text-warning-text dark:text-warning-300',
    bad: 'text-danger-600 dark:text-danger-400',
    info: 'text-info-text dark:text-info-300',
    off: 'text-mono-500',
};

const Chip = ({ tone, children }) => {
    const tons = {
        ok: 'bg-success-soft text-success-text dark:bg-success-900/30 dark:text-success-300',
        warn: 'bg-warning-soft text-warning-text dark:bg-warning-900/30 dark:text-warning-300',
        off: 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300',
        bad: 'bg-danger-soft text-danger-text dark:bg-danger-900/30 dark:text-danger-300',
    };
    return (
        <span
            data-oauth-chip
            className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${tons[tone] || tons.off}`}
        >
            {children}
        </span>
    );
};

/** Um passo do modal: trechos de texto e trechos em destaque (`{ strong }`). */
const StepText = ({ parts }) =>
    parts.map((part, index) => (typeof part === 'string' ? part : <strong key={index}>{part.strong}</strong>));

/** O accordion dos dois modais: fechado por padrão, o título é o único texto à vista. */
const Accordion = ({ title, children, ...rest }) => (
    <details {...rest} className="mt-2 rounded-lg border border-mono-200 px-3 py-1.5 dark:border-mono-800">
        <summary className="cursor-pointer select-none text-[11px] font-semibold text-mono-600 dark:text-mono-300">
            {title}
        </summary>
        <div className="mt-1.5 space-y-1 pb-1 text-[11px] text-mono-500">{children}</div>
    </details>
);

/**
 * A URL exata a cadastrar no aplicativo do provedor para a autorização voltar sozinha (o callback DESTA instância,
 * que o status informa), com o botão de copiar. Só existe para TikTok Shop e Shopee.
 */
const RegisterUrl = ({ registration }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(registration.url);
            setCopied(true);
        } catch {
            // Sem área de transferência (http fora de localhost, permissão negada): a URL está na tela para copiar à mão.
            setCopied(false);
        }
    };
    return (
        <div data-oauth-register className="text-[10px] text-mono-400">
            Para voltar sozinho, cadastre em {registration.where}:{' '}
            <code className="break-all font-mono text-mono-600 dark:text-mono-300">{registration.url}</code>{' '}
            <button type="button" onClick={copy} className="text-info-600 underline dark:text-info-300">
                {copied ? 'Copiado' : 'Copiar'}
            </button>
            {registration.domainNote ? <span className="block">{registration.domainNote}</span> : null}
        </div>
    );
};

const Dialog = ({ kind, label, children }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-oauth-modal={kind}
    >
        <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-mono-200 bg-white p-4 shadow-xl dark:border-mono-800 dark:bg-mono-950">
            {children}
        </div>
    </div>
);

const SECONDARY_BUTTON =
    'rounded-md border border-mono-300 px-3 py-1 text-[11px] font-semibold text-mono-600 disabled:opacity-40 dark:border-mono-700 dark:text-mono-300';

/** A palavra do ambiente na linha ("teste", "produção") — só quem a declara (a Shopee). */
const EnvironmentTag = ({ environment }) => {
    const tons = {
        warn: 'border-warning-500 text-warning-text dark:text-warning-300',
        off: 'border-mono-300 text-mono-500 dark:border-mono-700 dark:text-mono-400',
    };
    return (
        <span
            data-oauth-env={environment.apiEnv}
            className={`inline-flex shrink-0 rounded-full border px-1.5 py-0 text-[10px] font-semibold ${tons[environment.tone] || tons.off}`}
        >
            {environment.label}
        </span>
    );
};

/** A linha de um provedor — a MESMA para os quatro. */
const ConnectionRow = ({
    provider,
    connection,
    environment,
    configured,
    canDisconnect,
    busy,
    onDetails,
    onConnect,
    onDisconnect,
}) => (
    <div data-oauth-row={provider.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
        <span className="w-24 shrink-0 text-xs font-semibold text-black dark:text-white">{provider.label}</span>
        <Chip tone={connection.chip.tone}>{connection.chip.label}</Chip>
        {environment ? <EnvironmentTag environment={environment} /> : null}
        <span
            data-oauth-account
            title={connection.account}
            className="min-w-0 flex-1 truncate text-[11px] text-mono-500"
        >
            {connection.account}
        </span>
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={onDetails}
                className="text-[10px] text-info-600 underline dark:text-info-300"
            >
                Detalhes
            </button>
            <button
                type="button"
                disabled={!configured || busy}
                onClick={onConnect}
                className="rounded-md border border-info-500 px-2.5 py-1 text-[11px] font-semibold text-info-600 transition hover:bg-info-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 dark:text-info-300"
            >
                {busy ? '…' : connection.connected ? 'Reautenticar' : 'Conectar'}
            </button>
            <button
                type="button"
                disabled={!canDisconnect || busy}
                onClick={onDisconnect}
                className="rounded-md border border-mono-300 px-2.5 py-1 text-[11px] font-semibold text-mono-600 transition hover:border-danger-500 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
            >
                Desconectar
            </button>
        </div>
    </div>
);

/**
 * O modal da conexão — o MESMO para os quatro, e o único lugar que diz "conectado": abre sozinho quando a autorização
 * conclui (`success`) e pelo "Detalhes" da linha. Lê o status atual do provedor; nunca há token nele.
 */
const ConnectionModal = ({ provider, status, success, onClose, onPaste }) => {
    const connection = DescribeOAuthConnection(provider, status);
    const scopes = DescribeOAuthScopes(provider, status);
    const registration = DescribeOAuthRegistration(provider, status);
    const title = success ? provider.connectedText.replace(/\.$/, '') : provider.label;
    return (
        <Dialog kind="connection" label={`${provider.label} — conexão`}>
            <div className="flex items-center gap-2">
                <p data-oauth-title className="text-sm font-semibold text-black dark:text-white">
                    {title}
                </p>
                <Chip tone={connection.chip.tone}>{connection.chip.label}</Chip>
            </div>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                <dt className="text-mono-400">Conta</dt>
                <dd data-oauth-modal-account className="text-mono-700 dark:text-mono-200">
                    {connection.account}
                </dd>
                <dt className="text-mono-400">Validade</dt>
                <dd data-oauth-modal-validity className="text-mono-700 dark:text-mono-200">
                    {connection.validity}
                </dd>
            </dl>
            <Accordion data-oauth-scopes title={scopes.reported ? `Escopos (${scopes.items.length})` : 'Escopos'}>
                {scopes.reported ? (
                    <ul className="max-h-56 space-y-0.5 overflow-y-auto">
                        {scopes.items.map((item) => (
                            <li key={item} className="font-mono text-[10px] text-mono-700 dark:text-mono-200">
                                {item}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>{scopes.text}</p>
                )}
                {scopes.missing.length > 0 ? (
                    <p className={TEXT_TONES.warn}>Faltam: {scopes.missing.join(', ')}</p>
                ) : null}
            </Accordion>
            {connection.notes.length > 0 || registration ? (
                <Accordion data-oauth-notes title="Detalhes">
                    {connection.notes.map((note) => (
                        <p key={note.text} className={TEXT_TONES[note.tone] || TEXT_TONES.off}>
                            {note.text}
                        </p>
                    ))}
                    {registration ? <RegisterUrl registration={registration} /> : null}
                </Accordion>
            ) : null}
            <div className="mt-3 flex justify-end gap-2">
                {!success && status?.configured ? (
                    <button type="button" onClick={onPaste} className={`mr-auto ${SECONDARY_BUTTON}`}>
                        Colar URL de retorno
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md bg-info-500 px-3 py-1 text-[11px] font-semibold text-white"
                >
                    Fechar
                </button>
            </div>
        </Dialog>
    );
};

/** O modal de colar a URL de retorno — o MESMO para os quatro; os passos de cada provedor ficam atrás de "Como fazer". */
const PasteModal = ({ paste, registration, onChange, onAuthorize, onCancel, onConclude }) => {
    const { provider } = paste;
    return (
        <Dialog kind="paste" label={`Concluir autorização ${provider.ofLabel}`}>
            <p className="text-sm font-semibold text-black dark:text-white">Colar URL de retorno — {provider.label}</p>
            {paste.authorizeUrl ? (
                <p className="mt-1 text-[11px] text-mono-500">
                    Autorização aberta em outra aba.{' '}
                    <a
                        href={paste.authorizeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-info-600 underline dark:text-info-300"
                    >
                        abrir de novo
                    </a>
                </p>
            ) : null}
            <textarea
                value={paste.url}
                onChange={(e) => onChange(e.target.value)}
                rows={3}
                aria-label="URL de retorno"
                placeholder={provider.callbackPlaceholder}
                className="mt-2 w-full rounded-lg border border-mono-300 bg-white p-2 font-mono text-[11px] text-mono-900 outline-none focus:ring-2 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
            />
            {paste.erro ? (
                <p data-oauth-paste-error className="mt-1 text-[11px] text-danger-600 dark:text-danger-400">
                    {paste.erro}
                </p>
            ) : null}
            <Accordion data-oauth-howto title="Como fazer">
                <ol className="space-y-1">
                    <li>
                        {paste.authorizeUrl
                            ? provider.openStepNote || 'Autorize na aba que abriu.'
                            : 'Cole a URL da página em que o navegador parou depois de autorizar. Ainda não autorizou? Use "Autorizar em outra aba".'}
                    </li>
                    {provider.modalSteps.map((parts, index) => (
                        <li key={index}>
                            <StepText parts={parts} />
                        </li>
                    ))}
                </ol>
                {registration ? <RegisterUrl registration={registration} /> : null}
            </Accordion>
            <div className="mt-3 flex justify-end gap-2">
                {paste.authorizeUrl ? null : (
                    <button
                        type="button"
                        onClick={onAuthorize}
                        disabled={paste.enviando}
                        className={`mr-auto ${SECONDARY_BUTTON}`}
                    >
                        Autorizar em outra aba
                    </button>
                )}
                <button type="button" onClick={onCancel} className={SECONDARY_BUTTON}>
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={onConclude}
                    disabled={!paste.url.trim() || paste.enviando}
                    className="rounded-md bg-info-500 px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                >
                    {paste.enviando ? 'Concluindo…' : 'Concluir'}
                </button>
            </div>
        </Dialog>
    );
};

const ConnectionsCard = () => {
    const [status, setStatus] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [ocupado, setOcupado] = useState(null);
    // O aviso do cartão — o ÚNICO lugar das mensagens que não são "conectado": `{ tone, text }`.
    const [aviso, setAviso] = useState(null);
    // O modal de colar: `{ provider, authorizeUrl, url, erro, enviando }`.
    const [colar, setColar] = useState(null);
    // O modal da conexão: `{ key, success }` — `success` quando acabou de conectar.
    const [painel, setPainel] = useState(null);
    // O React.StrictMode monta os efeitos duas vezes em desenvolvimento, e o `state` é de uso único no servidor: a
    // conclusão da volta só pode sair uma vez por carga de página.
    const retornoTratado = useRef(false);
    // O `state` do Conectar para o plano B (colar) de TikTok Shop e Shopee, por provedor — só na memória desta página.
    const statesDoColar = useRef({});
    // O endereço que as aplicações do ML e do Bling têm cadastrado. Editável porque cada parceiro pode ter cadastrado
    // outro — e trocar aqui é mais rápido que mexer no painel do provider.
    const [redirectPadrao, setRedirectPadrao] = useState('https://www.greycom.com.br/');

    const carregar = useCallback(async () => {
        try {
            setErro(null);
            const res = await api.get('/integrations/oauth/status?identify=true');
            setStatus((res?.data ?? res)?.providers || {});
        } catch (e) {
            setErro(e?.message || 'Não foi possível ler o estado das conexões.');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    /**
     * A VOLTA AUTOMÁTICA. Nesta ordem, e tudo antes do primeiro `await`:
     *  1. lê o retorno da barra e a LIMPA (`history.replaceState`): o código não fica na barra nem no histórico, e
     *     a segunda montagem do StrictMode já não acha nada;
     *  2. TIRA a pendência desta aba (lê e apaga — uma tentativa por Conectar);
     *  3. decide pelas três regras; só então chama o `complete`. Deu certo: o modal da conexão.
     */
    useEffect(() => {
        if (retornoTratado.current) {
            return;
        }
        const chegou = ReadOAuthReturn(window.location);
        if (!chegou) {
            return;
        }
        retornoTratado.current = true;
        window.history.replaceState(window.history.state, '', StripOAuthReturn(window.location));

        const provider = OAUTH_PROVIDERS.find((entry) => entry.key === chegou.providerKey);
        const decisao = ResolveOAuthReturn({
            callbackQuery: chegou.callbackQuery,
            pendingState: TakePendingOAuthState(tabStorage(), provider.key),
        });
        if (!decisao.send) {
            setAviso({ tone: 'warn', text: `${provider.label}: ${RETURN_REFUSAL_TEXTS[decisao.reason]}` });
            return;
        }

        setOcupado(provider.key);
        setAviso({ tone: 'off', text: `${provider.label}: concluindo a autorização…` });
        (async () => {
            try {
                await api.post(`/integrations/oauth/${provider.key}/complete`, decisao.body);
                setAviso(null);
                await carregar();
                setPainel({ key: provider.key, success: true });
            } catch (e) {
                setAviso({
                    tone: 'bad',
                    text: `${provider.label}: ${DescribeOAuthCompletionError(provider, e, { automaticReturn: true })}`,
                });
            } finally {
                setOcupado(null);
            }
        })();
    }, [carregar]);

    /**
     * Pede ao servidor o link de autorização. Mercado Livre e Bling levam o `redirect_uri` da caixa do cabeçalho (o
     * endereço fixo cadastrado nas aplicações deles, que não é este backend); TikTok Shop e Shopee mandam `{}`.
     */
    const iniciar = async (provider) => {
        const res = await api.post(
            `/integrations/oauth/${provider.key}/start`,
            BuildOAuthStartBody(provider, redirectPadrao),
        );
        const dados = res?.data ?? res;
        if (!dados?.authorize_url) {
            throw new Error('O servidor não devolveu a URL de autorização.');
        }
        return dados;
    };

    /** Aba nova + modal de colar: o fluxo de sempre do Mercado Livre e do Bling, e o plano B dos outros dois. */
    const abrirEmOutraAba = (provider, dados) => {
        if (provider.returnMode === 'automatic') {
            statesDoColar.current[provider.key] = dados.state ?? null;
        }
        setColar({ provider, authorizeUrl: dados.authorize_url, url: '', erro: null, enviando: false });
        // Aba nova: trocar a aba atual faria perder o painel — e o modal.
        window.open(dados.authorize_url, '_blank', 'noopener');
    };

    const reautenticar = async (provider) => {
        setOcupado(provider.key);
        setAviso(null);
        try {
            const dados = await iniciar(provider);
            // Retorno automático: sai para a autorização NA MESMA ABA (a sessão mora no localStorage e sobrevive),
            // com o `state` guardado NESTA ABA antes de sair — a volta só conclui com ele. Não dá para voltar
            // sozinho quando a Redirect URL da Shopee não é a deste melidash (`returns_here: false`) ou quando o
            // navegador nega o sessionStorage: aí é aba nova e colar.
            const voltaSozinho =
                provider.returnMode === 'automatic' &&
                status?.[provider.key]?.returns_here !== false &&
                SavePendingOAuthState(tabStorage(), provider.key, dados.state);
            if (voltaSozinho) {
                NavigateSameTab(dados.authorize_url);
                return;
            }
            abrirEmOutraAba(provider, dados);
        } catch (e) {
            setAviso({
                tone: 'bad',
                text: `${provider.label}: ${e?.message || 'Não foi possível iniciar a autorização.'}`,
            });
        } finally {
            setOcupado(null);
        }
    };

    /**
     * "Colar URL de retorno" (Detalhes): o modal de colar, sem chamar o servidor. No TikTok Shop e na Shopee, se houve
     * um Conectar que saiu desta aba, o `state` dele sai do sessionStorage e fica na memória, para o colar de uma URL
     * que não traga `state`.
     */
    const abrirColar = (provider) => {
        if (provider.returnMode === 'automatic') {
            const guardado = TakePendingOAuthState(tabStorage(), provider.key);
            if (guardado) {
                statesDoColar.current[provider.key] = guardado;
            }
        }
        setAviso(null);
        setPainel(null);
        setColar({ provider, authorizeUrl: null, url: '', erro: null, enviando: false });
    };

    const autorizarEmOutraAba = async () => {
        const { provider } = colar;
        setColar((m) => ({ ...m, erro: null }));
        try {
            abrirEmOutraAba(provider, await iniciar(provider));
        } catch (e) {
            setColar((m) => (m ? { ...m, erro: e?.message || 'Não foi possível iniciar a autorização.' } : m));
        }
    };

    const concluir = async () => {
        if (!colar?.url.trim()) {
            return;
        }
        const { provider } = colar;
        setColar((m) => ({ ...m, enviando: true, erro: null }));
        try {
            // Mercado Livre e Bling: só `callback_url`, como sempre. TikTok Shop e Shopee: o `state` do Conectar
            // vai junto SÓ quando a URL colada não traz nenhum (`BuildOAuthCompleteBody`).
            const body =
                provider.returnMode === 'automatic'
                    ? BuildOAuthCompleteBody(colar.url.trim(), statesDoColar.current[provider.key] ?? null)
                    : { callback_url: colar.url.trim() };
            await api.post(`/integrations/oauth/${provider.key}/complete`, body);
            delete statesDoColar.current[provider.key];
            setColar(null);
            setAviso(null);
            await carregar();
            setPainel({ key: provider.key, success: true });
        } catch (e) {
            setColar((m) => ({ ...m, enviando: false, erro: DescribeOAuthCompletionError(provider, e) }));
        }
    };

    const desconectar = async (provider) => {
        // Confirmação porque isto derruba a integração para a organização inteira. O que a pessoa perde muda por
        // provedor: no ML e no Bling os workers param de sincronizar; no TikTok Shop e na Shopee some o token deste
        // melidash, e a autorização continua valendo no provedor.
        if (!window.confirm(`Desconectar a conta ${provider.ofLabel}?\n\n${provider.disconnectConsequence}`)) {
            return;
        }
        setOcupado(provider.key);
        setAviso(null);
        try {
            await api.post(`/integrations/oauth/${provider.key}/disconnect`);
            setAviso({ tone: 'info', text: provider.disconnectedText });
            await carregar();
        } catch (e) {
            setAviso({ tone: 'bad', text: `${provider.label}: ${e?.message || 'Não foi possível desconectar.'}` });
        } finally {
            setOcupado(null);
        }
    };

    const provedorDoPainel = painel ? OAUTH_PROVIDERS.find((entry) => entry.key === painel.key) : null;

    return (
        <div className="overflow-hidden rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
            {colar ? (
                <PasteModal
                    paste={{
                        ...colar,
                        provider: ResolveOAuthProviderForStatus(colar.provider, status?.[colar.provider.key]),
                    }}
                    registration={DescribeOAuthRegistration(
                        ResolveOAuthProviderForStatus(colar.provider, status?.[colar.provider.key]),
                        status?.[colar.provider.key],
                    )}
                    onChange={(url) => setColar((m) => ({ ...m, url }))}
                    onAuthorize={autorizarEmOutraAba}
                    onCancel={() => setColar(null)}
                    onConclude={concluir}
                />
            ) : null}

            {provedorDoPainel ? (
                <ConnectionModal
                    provider={ResolveOAuthProviderForStatus(provedorDoPainel, status?.[provedorDoPainel.key])}
                    status={status?.[provedorDoPainel.key]}
                    success={painel.success}
                    onClose={() => setPainel(null)}
                    onPaste={() => abrirColar(provedorDoPainel)}
                />
            ) : null}

            <div className="flex items-center justify-between border-b border-mono-100 px-4 py-2.5 dark:border-mono-900">
                <p className="text-sm font-semibold text-black dark:text-white">Conexões</p>
                <div className="flex items-center gap-2">
                    {/* O redirect que as aplicações do ML e do Bling têm cadastrado; editável porque muda por parceiro. */}
                    <input
                        value={redirectPadrao}
                        onChange={(e) => setRedirectPadrao(e.target.value)}
                        aria-label="Redirect URI cadastrada na aplicação do provider"
                        title="Mercado Livre e Bling: precisa ser exatamente a URL cadastrada na aplicação do provider"
                        className="w-[220px] rounded-md border border-mono-300 px-2 py-0.5 font-mono text-[10px] text-mono-700 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-200"
                    />
                    <button
                        type="button"
                        onClick={carregar}
                        className="rounded-md border border-mono-300 px-2 py-0.5 text-[10px] font-semibold text-mono-600 transition hover:border-info-500 hover:text-info-600 dark:border-mono-700 dark:text-mono-300"
                    >
                        Verificar
                    </button>
                </div>
            </div>

            {aviso ? (
                <p
                    data-oauth-notice
                    aria-live="polite"
                    className={`border-b border-mono-100 px-4 py-2 text-[11px] dark:border-mono-900 ${
                        TEXT_TONES[aviso.tone] || TEXT_TONES.off
                    }`}
                >
                    {aviso.text}
                </p>
            ) : null}

            {carregando ? (
                <p className="px-4 py-6 text-center text-xs text-mono-400">Consultando…</p>
            ) : erro ? (
                <p className="px-4 py-6 text-center text-xs text-danger-600 dark:text-danger-400">{erro}</p>
            ) : (
                <div className="divide-y divide-mono-100 dark:divide-mono-900">
                    {OAUTH_PROVIDERS.map((provider) => {
                        const p = status?.[provider.key] || {};
                        const connection = DescribeOAuthConnection(provider, p);
                        if (!connection.visible) {
                            return null;
                        }
                        return (
                            <ConnectionRow
                                key={provider.key}
                                provider={provider}
                                connection={connection}
                                environment={DescribeOAuthEnvironment(provider, p)}
                                configured={Boolean(p.configured)}
                                canDisconnect={Boolean(p.token?.present) && !p.disconnected}
                                busy={ocupado === provider.key}
                                onDetails={() => setPainel({ key: provider.key, success: false })}
                                onConnect={() => reautenticar(provider)}
                                onDisconnect={() => desconectar(provider)}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ConnectionsCard;
